import React, { useEffect, useState, useContext, useImperativeHandle, forwardRef } from 'react';
import CreditCards from '../assets/images/credit-cards.png';
import Modal from './Modal';
import useModal from '../hooks/useModal';
import { LanguageContext } from "../context/LanguageProvider";
import ApiConstants from '../utils/ApiConstants';
import ErrorModal from './ErrorModal';
import { getErrorMessages } from '../utils/errorMessages';
import { useAuthenticatedApiFetch } from '../hooks/useAuthenticatedApiFetch';
import { useMsal } from "@azure/msal-react";
import { usePayableAmount } from "../hooks/usePayableAmount";
import { CONFIG } from "../config";
import { useSimulation } from "../context/SimulationContext";

const PaymentPaySafe = forwardRef(({
  client,
  product,
  productId,
  onPaymentPageLoad,
  validationErrors,
  setPaysafeFailed
}, ref) => {

  const { t, language } = useContext(LanguageContext);
  const { ERROR_TITLE, ERROR_MESSAGE, ERROR_TITLE_429, ERROR_MESSAGE_429 } = getErrorMessages(t);
  const { instance } = useMsal();
  const authenticatedApiFetch = useAuthenticatedApiFetch(instance);
  const { isSimulation, isSimulationLoading, setSimulationLoading } = useSimulation();

  const { isModalVisible: isBankModalVisible, modalContent: bankModalContent, openModal: openBankModal, closeModal: closeBankModal } = useModal();
  const { isModalVisible: isErrorModalVisible, modalContent: errorModalContent, openModal: openErrorModal, closeModal: closeErrorModal } = useModal();
  const {
    isModalVisible: isErrorModal429Visible,
    modalContent: errorModal429Content,
    openModal: openErrorModal429,
    closeModal: closeErrorModal429
  } = useModal();

  const [demoInstance, setDemoInstance] = useState(null);

  const getDefaultDay = () => {
    const today = new Date().getDate();
    return today > 28 ? 28 : today;
  };

  const [dateOfWithdraw, setDateOfWithdraw] = useState(getDefaultDay);

  const [dateOfWithdrawError, setDateOfWithdrawError] = useState(false);
  const [payerInfo, setPayerInfo] = useState(null);

  const payableAmount = usePayableAmount(authenticatedApiFetch, product, productId, client);


  const getSimulationData = () => ({
    cardNumber: '1111 1111 1111 1111',
    expiryDate: (() => {
      const date = new Date();
      date.setMonth(date.getMonth() + 6);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      return `${month} / ${year}`;
    })(),
    cvv: '123'
  });


  // --- Load PaySafe Script ---
useEffect(() => {
    
    onPaymentPageLoad();

    if (isSimulationLoading) {
      return;
    }

    if (isSimulation) {
      setDateOfWithdraw('15');
      return;
    }

    const loadPaysafe = async () => {
      const script = document.createElement('script');
      script.src = "https://hosted.paysafe.com/js/v1/latest/paysafe.min.js";
      
      script.onerror = () => {
        openErrorModal(ERROR_TITLE, ERROR_MESSAGE);
      };
      
      script.onload = () => {

        const username = CONFIG.REACT_APP_PAYSAFE_USERNAME;
        const password = CONFIG.REACT_APP_PAYSAFE_PASSWORD;

        const environment =
          window.env?.REACT_APP_PAYSAFE_LIVE_MODE === "true"
            ? "LIVE"
            : "TEST";

        const API_KEY = btoa(`${username}:${password}`);
        const accountId = window.env?.REACT_APP_PAYSAFE_ACCOUNT_ID;

        const options = {
          currencyCode: 'CAD',
          merchantRefNum: 'unique-transaction-id',
          environment,
          amount: payableAmount ? Math.round(payableAmount * 100) : 0,
          ...(accountId != null && environment === "LIVE" && {
            accounts: {
              default: accountId,
            },
          }),
          fields: {
            cardNumber: { selector: '#cardNumber' },
            expiryDate: { selector: '#expiryDate' },
            cvv: { selector: '#cvv' },
          },
        };

        paysafe.fields.setup(API_KEY, options)
          .then(instance => { 
            setDemoInstance(instance); 
            return instance.show(); 
          })
          .then(() => {
          })
          .catch((error) => {
            console.error('❌ PaySafe setup error:', error);
            openErrorModal(ERROR_TITLE, ERROR_MESSAGE);
          });
      };
      
      document.body.appendChild(script);
    };

    loadPaysafe();
  }, [isSimulation, isSimulationLoading]);

  // --- Fetch payer info dynamically when Pay Now is clicked ---
  const fetchLatestPayerInfo = async () => {
    try {
      const endpoint = ApiConstants.getPayerInfo(product, productId, client);
      const url = `${ApiConstants.BASE_URL}${endpoint}`;
      const response = await authenticatedApiFetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      });


      if (response.status === 200 && response.data) {
        setPayerInfo(response.data);
        return response.data;
      } else {
        openErrorModal(ERROR_TITLE, ERROR_MESSAGE);
        return null;
      }
    } catch (error) {
      console.error("Error fetching latest payer info:", error);
      openErrorModal(ERROR_TITLE, ERROR_MESSAGE);
      return null;
    }
  };

  // --- Expose methods to parent via ref ---
  useImperativeHandle(ref, () => ({


    isFormValid: () => {
      const hasValidationErrors = validationErrors && Object.keys(validationErrors).length > 0;
      const valid = !!dateOfWithdraw && !!demoInstance && !hasValidationErrors;
      return valid;
    },

    handlePayNow: async () => {
      if (!demoInstance || !dateOfWithdraw) {
        setDateOfWithdrawError(true);
        return false;
      }

      //  Fetch the most recent payer info right before payment
      const latestPayer = await fetchLatestPayerInfo();
      if (!latestPayer) return false;

      const now = new Date();
      const timestamp =
        now.getFullYear().toString() +
        String(now.getMonth() + 1).padStart(2, '0') +
        String(now.getDate()).padStart(2, '0') +
        String(now.getHours()).padStart(2, '0') +
        String(now.getMinutes()).padStart(2, '0') +
        String(now.getSeconds()).padStart(2, '0');

      const merchantRefNum = `${timestamp}${latestPayer.id}`;

      const tokenizationOptions = {
        amount: payableAmount ? Math.round(payableAmount * 100) : 0,
        transactionType: 'PAYMENT',
        paymentType: 'CARD',
        merchantRefNum,
        customerDetails: {
          firstName: latestPayer.firstName,
          lastName: latestPayer.lastName,
          billingDetails: {
            country: latestPayer.address?.countryCode || 'CA',
            zip: latestPayer.address?.zip || '',
            street: latestPayer.address?.line1 || '',
            city: latestPayer.address?.city || '',
            state: latestPayer.address?.addressState || '',
          },
        },
      };

      try {
        const result = await demoInstance.tokenize(tokenizationOptions);
        const endpoint = ApiConstants.getPaymentPaysafe(product, productId, client);
        const url = `${ApiConstants.BASE_URL}${endpoint}`;

        const response = await authenticatedApiFetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': '*/*' },
          body: JSON.stringify({
            paymentToken: result.token,
            day: parseInt(dateOfWithdraw, 10),
          }),
        });

        if (response.status === 429) {
          console.warn("Paysafe limited (429).");

          const { ERROR_TITLE_429, ERROR_MESSAGE_429 } = getErrorMessages(t);
          openErrorModal429(ERROR_TITLE_429, ERROR_MESSAGE_429, () => {
            setPaysafeFailed(true);
          });

          return false;
        }

        if (response.status === 200 && response.data === true) return true;
        openErrorModal(ERROR_TITLE, ERROR_MESSAGE);
        return false;
      } catch (err) {
        console.error('Paysafe payment error:', err);
        const fieldMsg = (err?.detailedMessage || err?.message || '').toLowerCase();
        let errorMessage = ERROR_MESSAGE;
        if (String(err?.code) === '9003' && fieldMsg) {
          if (fieldMsg.includes('cvv')) {
            errorMessage = t('payment_error_invalid_cvv');
          } else if (fieldMsg.includes('card number')) {
            errorMessage = t('payment_error_invalid_card_number');
          } else if (fieldMsg.includes('expiry date')) {
            errorMessage = t('payment_error_invalid_expiry_date');
          }
        }
        openErrorModal(ERROR_TITLE, errorMessage);
        return false;
      }
    },
  }));



 
  return (
    <div className='payment'>
      <div className='creditCard'>
        <div className='entry'>
          <label htmlFor="cardNumber">{t('card_number')}<img src={CreditCards} alt="Credit Cards" width={66} height={19} /></label>
          {isSimulation ? (
            <input
              type="text"
              className="inputField form-control"
              value={getSimulationData().cardNumber}
              disabled
              style={{ backgroundColor: '#f5f5f5' }}
            />
          ) : (
            <div id="cardNumber" className="inputField"></div>
          )}
        </div>

        <div className='d-flex justify-content-start align-items-start no-gutters'>
          <div className='entry expiration'>
            <label htmlFor="cardExpiry">{t('expiration_date')}</label>
            {isSimulation ? (
              <input
                type="text"
                className="inputField form-control"
                value={getSimulationData().expiryDate}
                disabled
                style={{ backgroundColor: '#f5f5f5' }}
              />
            ) : (
              <div id="expiryDate" className="inputField"></div>
            )}
          </div>

          <div className='entry security-number'>
            <label htmlFor="cardCVC">{t('security_number')}</label>
            <a href="#" className="question-icon" onClick={(e) => {
              e.preventDefault();
              openBankModal(t('security_number'), t('security_number_info'));
            }}>
              <i className="fa-solid fa-circle-question"></i>
            </a>
            <Modal
              isVisible={isBankModalVisible}
              title={bankModalContent.title}
              content={bankModalContent.content}
              onClose={closeBankModal}
            />
            <ErrorModal
              isVisible={isErrorModalVisible}
              title={errorModalContent.title}
              content={errorModalContent.content}
              onClose={closeErrorModal}
            />
            <Modal
              isVisible={isErrorModal429Visible}
              title={errorModal429Content.title}
              content={errorModal429Content.content}
              onClose={closeErrorModal429}
              actions={
                <button
                  type="button"
                  className="btn-purple"
                  onClick={closeErrorModal429}
                >
                  {t('ok')}
                </button>
              }
            />
            {isSimulation ? (
              <input
                type="text"
                className="inputField form-control"
                value={getSimulationData().cvv}
                disabled
                style={{ backgroundColor: '#f5f5f5' }}
              />
            ) : (
              <div id="cvv" className="inputField"></div>
            )}
          </div>
        </div>

        <div className="entry">
          <label htmlFor="dateOfWithdrawFixed">
            {t('date_of_withdrawal_fixed')}           
          </label>: &nbsp; <span>{dateOfWithdraw}</span>
         
        </div>
      </div>

    </div>

  );
});

export default PaymentPaySafe;